/**
 * 页面缓存状态 - Pinia store
 *
 * 配合 MainLayout 里的 <KeepAlive :include="cachedViews">，让标记了 meta.keepAlive 的页面
 * 在路由切换后保留筛选条件、分页、列表数据与滚动位置（排查场景下切走再切回需要「定格」）。
 * 缓存注册由 src/router/index.ts 的 afterEach 钩子完成，接入方式见该处注释。
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useKeepAliveStore = defineStore('keepAlive', () => {
  /** 需要缓存的页面组件名，按访问顺序累积（未访问过的页面不占缓存） */
  const cachedViews = ref<string[]>([]);
  /** 各缓存页面离开时的内容区滚动位置，key 为组件名 */
  const scrollTops = ref<Record<string, number>>({});

  /** 标记某页面需要缓存 */
  const addCache = (name: string) => {
    if (!cachedViews.value.includes(name)) cachedViews.value.push(name);
  };

  /** 移除单个页面的缓存，关闭标签页时调用，让重新打开的是干净页面而不是旧数据 */
  const removeCache = (name: string) => {
    cachedViews.value = cachedViews.value.filter((item) => item !== name);
    delete scrollTops.value[name];
  };

  /** 清空所有缓存，退出登录 / 切换账号时调用，避免新账号看到上一个账号的数据 */
  const clearCache = () => {
    cachedViews.value = [];
    scrollTops.value = {};
  };

  /** 记录页面离开时的滚动位置 */
  const saveScrollTop = (name: string, top: number) => {
    scrollTops.value[name] = top;
  };

  /** 读取页面上次的滚动位置，无记录时为 0 */
  const getScrollTop = (name: string) => scrollTops.value[name] ?? 0;

  return { cachedViews, addCache, removeCache, clearCache, saveScrollTop, getScrollTop };
});
